import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import QuoteForm from '../../../components/feature/QuoteForm';

export default function Sealcoating() {
  const benefits = [
    {
      title: 'Water Protection',
      description: 'Seals surface to prevent water infiltration that causes cracking and base damage.',
      icon: 'ri-drop-line',
    },
    {
      title: 'UV Protection',
      description: 'Blocks harmful UV rays that oxidize and deteriorate asphalt binder.',
      icon: 'ri-sun-line',
    },
    {
      title: 'Chemical Resistance',
      description: 'Protects against oil, gas, and chemical spills that break down asphalt.',
      icon: 'ri-flask-line',
    },
    {
      title: 'Extends Lifespan',
      description: 'Doubles pavement life from 15 to 30+ years with regular applications.',
      icon: 'ri-time-line',
    },
    {
      title: 'Enhances Appearance',
      description: 'Restores rich black color and smooth finish for improved curb appeal.',
      icon: 'ri-paint-brush-line',
    },
    {
      title: 'Cost Savings',
      description: 'Prevents expensive repairs and premature replacement with affordable maintenance.',
      icon: 'ri-money-dollar-circle-line',
    },
  ];

  const faqs = [
    {
      question: 'How much does asphalt sealcoating cost in Lincoln NE?',
      answer: 'Sealcoating costs $0.15-$0.35 per square foot for residential driveways. A typical 400 sq ft driveway costs $60-$140. Commercial parking lots cost $0.15-$0.30 per square foot. Prices include surface cleaning, crack filling, and two coats of premium sealer. This small investment prevents thousands in repair costs.',
    },
    {
      question: 'How often should I sealcoat my driveway in Lincoln Nebraska?',
      answer: 'Sealcoat every 2-3 years for optimal protection. New asphalt should cure 6-12 months before first sealcoating. High-traffic areas may need annual sealing. Lincoln\'s freeze-thaw cycles and UV exposure make regular sealcoating essential. Proper maintenance doubles pavement lifespan.',
    },
    {
      question: 'When is the best time to sealcoat asphalt?',
      answer: 'Sealcoat April through October when temperatures are consistently above 50°F. Ideal conditions are 60-85°F with low humidity and no rain forecast for 24 hours. We avoid extreme heat (above 90°F) that causes rapid drying. Spring and fall provide optimal conditions in Lincoln.',
    },
    {
      question: 'How long does sealcoating take to dry?',
      answer: 'Sealcoating dries in 24-48 hours depending on temperature and humidity. Stay off for 24 hours minimum, 48 hours for heavy vehicles. Cooler temperatures and high humidity extend drying time. We schedule work to minimize disruption and ensure proper curing before traffic.',
    },
    {
      question: 'Should I repair cracks before sealcoating?',
      answer: 'Yes! Crack filling before sealcoating is essential. Sealcoat is too thin to fill cracks - it\'s a protective coating, not a filler. We fill cracks with hot rubberized material, then sealcoat the entire surface. This combination provides maximum protection against water infiltration.',
    },
    {
      question: 'What is pavement resurfacing and when is it needed?',
      answer: 'Resurfacing (overlay) involves applying 1.5-2 inches of new asphalt over existing pavement. It\'s needed when surface is worn but base is stable. Resurfacing costs $2-$5 per square foot versus $7-$12 for complete replacement. It provides fresh surface that lasts 10-15 years.',
    },
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, sans-serif' }}>
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-primary-500 text-sm mb-4 tracking-wider">Services / Sealcoating</p>
            <h1 className="text-5xl md:text-6xl font-black mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Sealcoating & Resurfacing
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Professional asphalt sealcoating and pavement resurfacing services in Lincoln, Nebraska
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <div>
              <img
                src="https://public.readdy.ai/ai/img_res/8442cebdcdc06741ac1217cbe6d60508.jpg"
                alt="Lincoln NE asphalt sealcoating services"
                className="w-full h-96 object-cover rounded-2xl shadow-xl"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Protect Your Asphalt Investment
              </h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                Sealcoating is the most important maintenance service for asphalt pavement. This protective coating shields your driveway or parking lot from water, UV rays, chemicals, and traffic wear that cause deterioration. Regular sealcoating doubles pavement life while maintaining attractive appearance.
              </p>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                We specialize in professional sealcoating and resurfacing services that protect your pavement investment. Using premium materials and proven techniques, we deliver lasting results that keep your asphalt looking great and performing well for decades.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Serving Lincoln and Lancaster County for over 20 years, we provide expert sealcoating services for residential driveways and commercial parking lots. Our experienced crews work efficiently to minimize disruption while delivering quality results you can see and trust.
              </p>
              <a href="tel:3084705637" className="inline-block bg-primary-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap">
                CALL (308) 470-5637
              </a>
            </div>
          </div>

          {/* Benefits Grid */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold mb-12 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Sealcoating Benefits
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="bg-gray-50 p-8 rounded-2xl border-2 border-gray-200 hover:border-primary-600 transition-all">
                  <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mb-6">
                    <i className={`${benefit.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {benefit.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Detailed Content */}
          <div className="prose prose-lg max-w-none mb-16">
            <h2 className="text-4xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Asphalt Sealcoating in Lincoln NE
            </h2>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Asphalt pavement is constantly under attack. Water infiltrates through cracks and pores, freezing and expanding in winter. UV rays oxidize the asphalt binder, turning it gray and brittle. Oil and gas spills dissolve the binder. Traffic wears away the surface. Without protection, asphalt deteriorates rapidly in Lincoln's harsh climate.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Sealcoating provides a protective barrier against these threats. This coal tar or asphalt emulsion coating seals the surface, preventing water infiltration, blocking UV rays, resisting chemicals, and creating a smooth, durable finish. Regular sealcoating is the most cost-effective way to extend pavement life and maintain appearance.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Professional Sealcoating Process
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Quality sealcoating requires proper preparation and application. We start with thorough cleaning using power blowers to remove dirt, debris, and vegetation. Clean surface ensures proper sealer adhesion. We trim grass edges and remove any loose material that would prevent bonding.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Next, we fill all cracks with hot rubberized crack filler. This prevents water infiltration and stops cracks from spreading. Crack filling before sealcoating is essential - sealcoat is too thin to fill cracks effectively. We clean cracks, heat filler to 350-400°F, and apply with professional equipment.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              We apply two coats of premium coal tar or asphalt emulsion sealer. The first coat penetrates and seals the surface. The second coat provides additional protection and uniform appearance. We use professional squeegees or spray equipment for even application. Edges are hand-brushed for complete coverage.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              After application, sealer needs 24-48 hours to cure before traffic. We barricade driveways and post signs to prevent premature traffic. Proper curing is critical for durability. We schedule work to minimize inconvenience and ensure proper drying conditions.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              When to Sealcoat Your Asphalt
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              New asphalt should cure 6-12 months before first sealcoating. Fresh asphalt contains oils that must evaporate before sealing. Sealcoating too early traps these oils, preventing proper curing. We inspect new asphalt and recommend optimal timing for first application.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              After initial sealcoating, reapply every 2-3 years for optimal protection. High-traffic areas may need annual sealing. Signs your asphalt needs sealcoating: gray or faded appearance, small cracks appearing, surface feels rough or oxidized, water no longer beads on surface. Don't wait until damage is severe - preventive maintenance is most effective.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Sealcoat April through October when temperatures are consistently above 50°F. Ideal conditions are 60-85°F with low humidity and no rain forecast for 24 hours. We avoid extreme heat (above 90°F) that causes rapid drying and poor adhesion. Spring and fall provide optimal conditions in Lincoln.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Coal Tar vs Asphalt Emulsion Sealers
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Coal tar sealers are the industry standard for durability and protection. They resist gas and oil better, provide superior UV protection, and last longer than asphalt emulsion. Coal tar creates a deep black finish that enhances appearance. Most commercial properties use coal tar for maximum protection.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Asphalt emulsion sealers are more environmentally friendly and work well for residential driveways. They're easier to apply, dry faster, and cost slightly less. Emulsion sealers provide good protection but may need more frequent reapplication. We help customers choose the best sealer for their needs and budget.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Asphalt Resurfacing Services
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Resurfacing (also called overlay) involves applying a new layer of asphalt over existing pavement. This is ideal when the surface is worn, oxidized, or has minor cracking, but the base remains stable. Resurfacing costs 50-70% less than complete replacement while providing a fresh, smooth surface.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Our resurfacing process: mill the existing surface to create a bonding surface and proper height, repair any base problems or potholes, clean thoroughly, apply tack coat for adhesion, and pave with 1.5-2 inches of new asphalt. The result looks and performs like new pavement at a fraction of replacement cost.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Resurfacing costs $2-$5 per square foot versus $7-$12 for complete replacement. A typical 400 sq ft driveway costs $800-$2,000 to resurface versus $2,800-$4,800 for replacement. Resurfaced pavement lasts 10-15 years with proper maintenance. It's the smart choice when base is sound but surface is worn.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Sealcoating Cost and Value
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Sealcoating costs $0.15-$0.35 per square foot for residential driveways. A typical 400 sq ft driveway costs $60-$140 including cleaning, crack filling, and two coats of sealer. Commercial parking lots cost $0.15-$0.30 per square foot. Prices vary based on size, condition, and sealer type.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              This small investment provides tremendous value. Sealcoating every 2-3 years doubles pavement life from 15 to 30+ years. A $100 sealcoating service prevents $2,000 in repairs and $5,000 in premature replacement. The math is simple: spend a little on maintenance or a lot on replacement.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Beyond cost savings, sealcoating maintains curb appeal and property value. A well-maintained driveway enhances your home's appearance and makes a positive impression. Neglected pavement looks bad and suggests poor property maintenance. Regular sealcoating is smart financial and aesthetic investment.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              DIY vs Professional Sealcoating
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              DIY sealcoating products from big box stores seem economical but often cost more long-term. Consumer-grade sealers are thinner (30-40% solids versus 50-60% for professional), less durable, and harder to apply properly. Poor application leaves streaks, thin spots, and inadequate protection that fails within a year.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Professional sealcoating uses commercial-grade materials, proper surface preparation, professional application equipment, and proven techniques. We apply two coats for complete coverage and lasting protection. Our work is guaranteed and lasts 2-3 years versus 6-12 months for DIY. The small price difference delivers far superior results.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Maintenance After Sealcoating
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Sealcoated pavement requires minimal maintenance. Sweep regularly to remove debris. Clean oil or gas spills promptly with detergent and water. Avoid sharp objects that could scratch the surface. Don't use metal shovels or snowplows that damage the sealer - use plastic or rubber-edged tools.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Inspect annually for new cracks or damage. Fill cracks promptly to prevent water infiltration. Resealcoat every 2-3 years to maintain protection. With proper care, your asphalt will last 30+ years while looking great and performing well.
            </p>
          </div>

          {/* Before/After */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold mb-12 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Sealcoating Results
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=worn%20faded%20gray%20asphalt%20driveway%20with%20oxidation%20and%20minor%20cracks%20before%20sealcoating%20showing%20deteriorated%20surface%20in%20Lincoln%20Nebraska%20with%20clean%20simple%20light%20background&width=800&height=600&seq=113&orientation=landscape"
                  alt="Before sealcoating"
                  className="w-full h-80 object-cover rounded-2xl shadow-xl"
                />
                <p className="text-center mt-4 text-xl font-semibold text-gray-700">Before Sealcoating</p>
              </div>
              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=brand%20new%20pristine%20black%20asphalt%20driveway%20surface%20with%20mirror%20like%20glossy%20finish%20completely%20smooth%20without%20any%20imperfections%20or%20damage%20showing%20perfect%20uniform%20jet%20black%20color%20and%20flawless%20professional%20sealcoating%20application%20on%20residential%20property%20with%20soft%20neutral%20beige%20background%20highlighting%20the%20immaculate%20pavement%20quality&width=800&height=600&seq=114c&orientation=landscape"
                  alt="After sealcoating"
                  className="w-full h-80 object-cover rounded-2xl shadow-xl"
                />
                <p className="text-center mt-4 text-xl font-semibold text-gray-700">After Sealcoating</p>
              </div>
            </div>
          </div>

          {/* FAQs */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold mb-12 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-gray-50 p-8 rounded-2xl border-2 border-gray-200">
                  <h3 className="text-2xl font-bold mb-4 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {faq.question}
                  </h3>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Quote Form */}
          <div className="bg-gray-50 p-12 rounded-2xl">
            <h2 className="text-4xl font-bold mb-6 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Schedule Your Sealcoating Service
            </h2>
            <p className="text-xl text-gray-700 text-center mb-8">
              Protect your pavement with professional sealcoating
            </p>
            <QuoteForm />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}